import { z } from 'zod';

/**
 * Normalizes a Twitter/X handle or URL to a full URL
 * Accepts: @username, username, or full URL
 */
export function normalizeTwitterUrl(input: string): string {
  if (!input) return '';
  
  const trimmed = input.trim();
  
  // Already a full URL
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    // Validate it's a twitter/x URL
    if (trimmed.includes('twitter.com') || trimmed.includes('x.com')) {
      return trimmed;
    }
    throw new Error('Must be a valid Twitter/X URL');
  }
  
  // Remove @ if present and get username
  const username = trimmed.replace('@', '');
  
  // Validate username format (alphanumeric and underscores, 1-15 chars)
  if (!/^[a-zA-Z0-9_]{1,15}$/.test(username)) {
    throw new Error('Invalid Twitter/X username format');
  }
  
  return `https://twitter.com/${username}`;
}

/**
 * Normalizes a LinkedIn profile URL
 * Accepts: username, /in/username, or full URL
 */
export function normalizeLinkedInUrl(input: string): string {
  if (!input) return '';
  
  const trimmed = input.trim();
  
  // Already a full URL
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    // Validate it's a LinkedIn URL
    if (trimmed.includes('linkedin.com')) {
      return trimmed;
    }
    throw new Error('Must be a valid LinkedIn URL');
  }
  
  // Remove leading /in/ if present
  let username = trimmed.replace(/^\/in\//, '');
  
  // Validate username format (alphanumeric, hyphens, max 100 chars)
  if (!/^[a-zA-Z0-9-]{3,100}$/.test(username)) {
    throw new Error('Invalid LinkedIn username format');
  }
  
  return `https://linkedin.com/in/${username}`;
}

/**
 * Validates and normalizes a website URL
 */
export function normalizeWebsiteUrl(input: string): string {
  if (!input) return '';
  
  let trimmed = input.trim();
  
  // Add https:// if no protocol specified
  if (!trimmed.startsWith('http://') && !trimmed.startsWith('https://')) {
    trimmed = `https://${trimmed}`;
  }
  
  // Validate URL format
  try {
    const url = new URL(trimmed);
    
    // Must have valid domain
    if (!url.hostname || url.hostname.length < 3) {
      throw new Error('Invalid domain name');
    }
    
    // Must have at least one dot in domain
    if (!url.hostname.includes('.')) {
      throw new Error('Invalid domain format');
    }
    
    return url.toString();
  } catch (error) {
    throw new Error('Invalid website URL format');
  }
}

/**
 * Zod custom transformers for social media URLs
 */
export const twitterUrlSchema = z
  .string()
  .trim()
  .max(200, 'Twitter URL is too long')
  .optional()
  .or(z.literal(''))
  .transform((val) => {
    if (!val) return '';
    try {
      return normalizeTwitterUrl(val);
    } catch (error: any) {
      throw new z.ZodError([{
        code: 'custom',
        message: error.message,
        path: []
      }]);
    }
  });

export const linkedInUrlSchema = z
  .string()
  .trim()
  .max(300, 'LinkedIn URL is too long')
  .optional()
  .or(z.literal(''))
  .transform((val) => {
    if (!val) return '';
    try {
      return normalizeLinkedInUrl(val);
    } catch (error: any) {
      throw new z.ZodError([{
        code: 'custom',
        message: error.message,
        path: []
      }]);
    }
  });

export const websiteUrlSchema = z
  .string()
  .trim()
  .max(500, 'Website URL is too long')
  .optional()
  .or(z.literal(''))
  .transform((val) => {
    if (!val) return '';
    try {
      return normalizeWebsiteUrl(val);
    } catch (error: any) {
      throw new z.ZodError([{
        code: 'custom',
        message: error.message,
        path: []
      }]);
    }
  });

/**
 * Validates all social media links at once
 */
export function validateSocialLinks(links: {
  twitter?: string;
  linkedin?: string;
  website?: string;
}): {
  twitter: string;
  linkedin: string;
  website: string;
  errors: string[];
} {
  const errors: string[] = [];
  let twitter = '';
  let linkedin = '';
  let website = '';
  
  // Validate Twitter
  if (links.twitter) {
    try {
      twitter = normalizeTwitterUrl(links.twitter);
    } catch (error: any) {
      errors.push(`Twitter: ${error.message}`);
    }
  }
  
  // Validate LinkedIn
  if (links.linkedin) {
    try {
      linkedin = normalizeLinkedInUrl(links.linkedin);
    } catch (error: any) {
      errors.push(`LinkedIn: ${error.message}`);
    }
  }
  
  // Validate Website
  if (links.website) {
    try {
      website = normalizeWebsiteUrl(links.website);
    } catch (error: any) {
      errors.push(`Website: ${error.message}`);
    }
  }
  
  return { twitter, linkedin, website, errors };
}
